/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useState } from "react";
import LifeformPicker from "./LifeformPicker";
import PlantSpeciesDescription from "./PlantSpeciesDescription";
import EcologicalRelationships from "./EcologicalRelationships";

export default function ProfilePhotoViewer({
  openLocationPicker,
  isOwnProfile,
  canEditObservationMetadata = false,
  canReclassifyObservations = false,
  canDeleteObservations = false,
  deletingObservation = false,
  openAdminDeleteObservation,
  deleteRecentUploadPhoto,
  deletingOwnObservation = false,
  favViewOpen,
  setFavViewOpen,
  favViewSpecies,
  setFavViewSpecies,
  favViewForm,
  setFavViewForm,
  favViewImageLoaded,
  setFavViewImageLoaded,
  favViewFullscreen,
  setFavViewFullscreen,
  favCollectionsPopoverOpen,
  setFavCollectionsPopoverOpen,
  editingForm,
  setEditingForm,
  editingSpecies,
  renamingPhoto,
  renameValue,
  setRenameValue,
  renameCommon,
  setRenameCommon,
  renameSaving,
  renameError,
  renameSuggestions,
  renameActiveIndex,
  renameActiveField,
  updateRenameSuggestions,
  handleRenameKeyDown,
  selectRenameSuggestion,
  enterPhotoEdit,
  exitPhotoEdit,
  savePhotoMetadata,
  enterPhotoRename,
  exitPhotoRename,
  handleRenameProfilePhoto,
  normalizeDate,
  formatDateForDisplay,
  hasMappedLocation,
  displayLocation,
  resolveTaxonomy,
  DAYS,
  MONTHS,
  YEARS,
  collections,
  collectionOwnerUid,
  recentUploadViewerIndex = null,
  recentUploadCount = 0,
  onPreviousRecentUpload,
  onNextRecentUpload,
}) {
  const [savingPhotoMetadata, setSavingPhotoMetadata] = useState(false);

  const viewedGenus = String(favViewSpecies?.name || "")
    .trim()
    .split(/\s+/)[0];

  const viewedTaxonomy = viewedGenus ? resolveTaxonomy?.(viewedGenus) : null;

  const isPlantSpecies =
    String(viewedTaxonomy?.kingdom || "").toLowerCase() === "plantae";

  if (!favViewOpen || !favViewSpecies || !favViewForm) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-start sm:items-center justify-center z-50 px-0 sm:px-4 pt-0 sm:pt-20 pb-0 sm:pb-6 overflow-y-auto"
      onClick={() => {
        setFavViewOpen(false);
        setFavViewFullscreen(false);
        setFavCollectionsPopoverOpen(false);
        setFavViewSpecies(null);
        setFavViewForm(null);
      }}
    >
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <div className="relative bg-white sm:bg-[#f4f6f8] rounded-none sm:rounded-[2rem] shadow-2xl w-screen sm:w-full max-w-none sm:max-w-5xl min-h-screen sm:min-h-0 max-h-none sm:max-h-[90vh] overflow-hidden">
          {!editingForm && !renamingPhoto && (
            <div
              className="absolute left-3 right-3 top-3 z-40 flex flex-wrap items-center justify-end gap-1.5 sm:left-auto sm:right-5 sm:top-5"
              onClick={(e) => e.stopPropagation()}
            >
              {(isOwnProfile || canEditObservationMetadata) && (
                <button
                  type="button"
                  onClick={() => enterPhotoEdit(favViewForm.url)}
                  className={`rounded-md px-2.5 py-1.5 text-[11px] font-medium shadow-sm backdrop-blur transition ${
                    isOwnProfile
                      ? "bg-white/85 text-slate-800 hover:bg-white"
                      : "bg-[#374049] text-white hover:bg-[#01767A]"
                  }`}
                >
                  {isOwnProfile ? "Edit" : "Admin Edit"}
                </button>
              )}

              {canReclassifyObservations && (
                <button
                  type="button"
                  onClick={() => enterPhotoRename(favViewForm.url)}
                  disabled={deletingObservation}
                  className={`rounded-md px-2.5 py-1.5 text-[11px] font-medium shadow-sm backdrop-blur transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    isOwnProfile
                      ? "bg-white/85 text-slate-800 hover:bg-white"
                      : "bg-[#01767A] text-white hover:bg-[#24C3BE]"
                  }`}
                >
                  {isOwnProfile ? "Rename" : "Admin Reclassify"}
                </button>
              )}

              {canDeleteObservations && (
                <button
                  type="button"
                  onClick={() => openAdminDeleteObservation?.(favViewForm.url)}
                  disabled={deletingObservation}
                  className="rounded-md border border-red-600 bg-red-600 px-2.5 py-1.5 text-[11px] font-medium text-white shadow-sm backdrop-blur transition hover:border-red-700 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deletingObservation ? "Deleting..." : "Admin Delete"}
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setFavViewOpen(false);
                  setFavViewFullscreen(false);
                  setFavCollectionsPopoverOpen(false);
                  setFavViewSpecies(null);
                  setFavViewForm(null);
                }}
                className="rounded-md bg-black/65 px-2.5 py-1.5 text-[11px] font-medium text-white shadow-sm backdrop-blur transition hover:bg-black/80"
              >
                Exit
              </button>
            </div>
          )}
          <div className="p-3 sm:p-5 overflow-y-auto max-h-none sm:max-h-[90vh]">
            {!editingForm && (
              <div className="flex justify-center mb-4 sm:mb-6">
                <div
                  className="relative w-full flex items-center justify-center bg-transparent"
                  style={{ minHeight: "40vh" }}
                  onClick={() => setFavViewFullscreen(true)}
                >
                  {!favViewImageLoaded && (
                    <div className="absolute flex items-center justify-center">
                      <div className="w-8 h-8 border-2 border-slate-300 border-t-[#1f3d2b] rounded-full animate-spin" />
                    </div>
                  )}

                  <div className="relative inline-block max-w-full">
                    <img
                      src={favViewForm.url}
                      alt={favViewSpecies.name}
                      onLoad={() => setFavViewImageLoaded(true)}
                      onError={() => setFavViewImageLoaded(true)}
                      className={`block object-contain max-w-full max-h-[62vh] transition-opacity duration-200 cursor-zoom-in ${
                        favViewImageLoaded ? "opacity-100" : "opacity-0"
                      }`}
                    />

                    {recentUploadViewerIndex !== null &&
                      !renamingPhoto &&
                      !favCollectionsPopoverOpen && (
                        <>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              onPreviousRecentUpload?.();
                            }}
                            disabled={recentUploadViewerIndex === 0}
                            aria-label="View previous recent upload"
                            className="absolute left-1 top-1/2 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/75 bg-[#374049]/85 pb-1 text-3xl leading-none text-white shadow-xl backdrop-blur-sm transition hover:bg-[#01767A] disabled:cursor-default disabled:opacity-25 sm:left-2 sm:h-12 sm:w-12 sm:text-4xl"
                          >
                            ‹
                          </button>

                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              onNextRecentUpload?.();
                            }}
                            disabled={
                              recentUploadViewerIndex >= recentUploadCount - 1
                            }
                            aria-label="View next recent upload"
                            className="absolute right-1 top-1/2 z-30 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/75 bg-[#374049]/85 pb-1 text-3xl leading-none text-white shadow-xl backdrop-blur-sm transition hover:bg-[#01767A] disabled:cursor-default disabled:opacity-25 sm:right-2 sm:h-12 sm:w-12 sm:text-4xl"
                          >
                            ›
                          </button>

                          <div className="pointer-events-none absolute bottom-2 left-1/2 z-30 -translate-x-1/2 rounded-full border border-white/50 bg-[#374049]/85 px-3 py-1.5 text-[10px] font-semibold text-white shadow-lg backdrop-blur-sm sm:text-[11px]">
                            {recentUploadViewerIndex + 1} / {recentUploadCount}
                          </div>
                        </>
                      )}
                  </div>
                </div>
              </div>
            )}
            {favViewFullscreen && (
              <div
                className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center"
                onClick={() => setFavViewFullscreen(false)}
              >
                <img
                  src={favViewForm.url}
                  alt={favViewSpecies.name}
                  className="max-w-[95vw] max-h-[95vh] object-contain cursor-zoom-out"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}

            {!editingForm && (
              <div className="mt-5 px-1 sm:px-2">
                <h3
                  className="text-3xl italic text-slate-900 leading-tight text-center"
                  style={{ fontFamily: "Cormorant Garamond" }}
                >
                  {favViewSpecies.name}
                  {favViewSpecies.name && (
                    <span className="not-italic text-slate-700">
                      {" "}
                      (
                      {(() => {
                        const family =
                          resolveTaxonomy(favViewSpecies.name.split(" ")[0])
                            ?.family || "family unknown";

                        return family.charAt(0).toUpperCase() + family.slice(1);
                      })()}
                      )
                    </span>
                  )}{" "}
                </h3>

                <p className="text-slate-600 mt-0.5 text-center">
                  {favViewSpecies.common || ""}
                </p>
              </div>
            )}

            {editingForm && editingForm.url === favViewForm.url && (
              <div className="mx-auto mt-4 mb-6 w-full max-w-5xl space-y-6">
                {/* ================= EDIT HEADER ================= */}
                <div className="relative flex flex-col border-b border-[#93ca87]/40 pb-4 pt-10 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:pt-0">
                  <div className="min-w-0 pr-0 sm:pr-4">
                    <h3
                      className="truncate text-3xl italic leading-tight text-slate-900"
                      style={{ fontFamily: "Cormorant Garamond" }}
                    >
                      {favViewSpecies.name || "—"}
                    </h3>

                    {favViewSpecies.common && (
                      <p className="mt-0.5 truncate text-sm text-slate-600">
                        {favViewSpecies.common}
                      </p>
                    )}
                  </div>

                  <div className="absolute right-0 top-0 flex shrink-0 items-center gap-1.5 sm:static sm:gap-2">
                    <button
                      type="button"
                      disabled={savingPhotoMetadata}
                      onClick={async () => {
                        if (savingPhotoMetadata) return;

                        setSavingPhotoMetadata(true);

                        try {
                          await savePhotoMetadata();
                        } finally {
                          setSavingPhotoMetadata(false);
                        }
                      }}
                      className={`rounded-md px-2.5 py-1.5 text-[11px] font-medium text-white shadow-sm transition sm:px-3 sm:text-xs ${
                        savingPhotoMetadata
                          ? "cursor-not-allowed bg-slate-400"
                          : "bg-[#1f3d2b] hover:bg-[#162c20]"
                      }`}
                    >
                      {savingPhotoMetadata ? "Saving..." : "Save"}
                    </button>

                    {isOwnProfile && recentUploadViewerIndex !== null && (
                      <button
                        type="button"
                        onClick={() => deleteRecentUploadPhoto?.(editingForm.url)}
                        disabled={deletingOwnObservation || savingPhotoMetadata}
                        className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-[11px] font-semibold text-red-700 shadow-sm transition hover:border-red-300 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 sm:px-3 sm:text-xs"
                      >
                        {deletingOwnObservation ? "Deleting..." : "Delete"}
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={exitPhotoEdit}
                      className="rounded-md border border-[#93ca87]/40 bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#374049] shadow-sm transition hover:bg-slate-50 sm:px-3 sm:text-xs"
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        exitPhotoEdit();
                        setFavViewOpen(false);
                        setFavViewFullscreen(false);
                        setFavCollectionsPopoverOpen(false);
                        setFavViewSpecies(null);
                        setFavViewForm(null);
                      }}
                      className="rounded-md bg-black/65 px-2.5 py-1.5 text-[11px] font-medium text-white shadow-sm transition hover:bg-black/80 sm:px-3 sm:text-xs"
                    >
                      Exit
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-8 border-b border-[#93ca87]/40 pb-10 md:flex-row">
                  {/* ================= IMAGE ================= */}
                  <div className="flex w-full flex-col md:w-56">
                    <img
                      src={editingForm.url}
                      alt={editingForm.label || favViewSpecies.name || "Photo"}
                      className="h-56 w-full object-cover border border-[#93ca87]/40 md:h-40"
                    />
                  </div>

                  {/* ================= METADATA ================= */}
                  <div className="min-w-0 flex-1 space-y-4">
                    {/* ================= DATE + LOCATION ================= */}
                    <div className="grid min-w-0 grid-cols-1 items-end gap-4 md:grid-cols-[2fr_3fr]">
                      <div className="flex h-full min-w-0 flex-col">
                        <div className="mb-1 text-xs font-semibold text-slate-600">
                          Date
                        </div>

                        <div className="mt-auto grid grid-cols-[0.9fr_1.6fr_1.1fr] gap-2">
                          <select
                            value={editingForm?.date?.day || "NA"}
                            onChange={(e) =>
                              setEditingForm({
                                ...editingForm,
                                date: {
                                  ...normalizeDate(editingForm.date),
                                  day: e.target.value,
                                },
                              })
                            }
                            className="h-[48px] w-full rounded-xl border border-[#93ca87]/40 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                          >
                            {DAYS.map((d) => (
                              <option key={d} value={d}>
                                {d}
                              </option>
                            ))}
                          </select>

                          <select
                            value={editingForm?.date?.month || "NA"}
                            onChange={(e) =>
                              setEditingForm({
                                ...editingForm,
                                date: {
                                  ...normalizeDate(editingForm?.date),
                                  month: e.target.value,
                                },
                              })
                            }
                            className="border border-slate-300 rounded-xl px-2 py-2 text-sm bg-white"
                          >
                            {MONTHS.map((month) => {
                              const monthValue =
                                typeof month === "object" ? month.value : month;

                              const monthLabel =
                                typeof month === "object" ? month.label : month;

                              return (
                                <option key={monthValue} value={monthValue}>
                                  {monthLabel}
                                </option>
                              );
                            })}
                          </select>

                          <select
                            value={editingForm?.date?.year || "NA"}
                            onChange={(e) =>
                              setEditingForm({
                                ...editingForm,
                                date: {
                                  ...normalizeDate(editingForm.date),
                                  year: e.target.value,
                                },
                              })
                            }
                            className="h-[48px] w-full rounded-xl border border-[#93ca87]/40 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                          >
                            {YEARS.map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="flex h-full min-w-0 flex-col">
                        <div className="mb-1 text-xs font-semibold text-slate-600">
                          Location
                        </div>

                        <button
                          type="button"
                          onClick={openLocationPicker}
                          className="mt-auto block h-[48px] w-full min-w-0 overflow-hidden rounded-xl border border-[#93ca87]/40 bg-white px-4 text-left text-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                        >
                          <span className="block w-full truncate">
                            {editingForm.locationName ||
                              editingForm.location ||
                              "Select location..."}
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* ================= FORM + ADDITIONAL INFORMATION ================= */}
                    <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[2fr_3fr]">
                      <LifeformPicker
                        scientificName={editingSpecies?.name || favViewSpecies?.name || ""}
                        value={editingForm.label || ""}
                        selectedKingdom={editingForm.lifeformKingdom || ""}
                        onChange={(value) => setEditingForm({ ...editingForm, label: value })}
                        onKingdomChange={(value) => setEditingForm({
                          ...editingForm, lifeformKingdom: value, label: "",
                        })}
                      />

                      <div className="relative min-w-0">
                        <div className="mb-1 text-xs font-semibold text-slate-600">
                          Additional Information
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            setEditingForm({
                              ...editingForm,
                              showAdditionalInfo:
                                !editingForm.showAdditionalInfo,
                            })
                          }
                          className="flex h-[48px] w-full min-w-0 items-center justify-between gap-3 rounded-xl border border-[#93ca87]/40 bg-white px-4 text-left text-sm text-[#374049] transition hover:bg-[#24c3be]/5 focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                        >
                          <span className="truncate">
                            {editingForm.showVariantField ||
                            editingForm.showCollectionsField ||
                            editingForm.showNotesField
                              ? "Manage optional fields"
                              : "Choose optional fields"}
                          </span>

                          <span
                            className={`shrink-0 text-lg text-[#24c3be] transition-transform ${
                              editingForm.showAdditionalInfo ? "rotate-180" : ""
                            }`}
                          >
                            ⌄
                          </span>
                        </button>

                        {editingForm.showAdditionalInfo && (
                          <div className="absolute left-0 top-full z-30 mt-2 w-full rounded-2xl border border-[#93ca87]/40 bg-white p-3 shadow-lg">
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                              <button
                                type="button"
                                onClick={() =>
                                  setEditingForm({
                                    ...editingForm,
                                    showVariantField:
                                      !editingForm.showVariantField,
                                  })
                                }
                                className={`rounded-xl border px-3 py-2 text-xs font-medium transition ${
                                  editingForm.showVariantField
                                    ? "border-[#24c3be] bg-[#24c3be] text-white"
                                    : "border-[#93ca87]/40 bg-white text-[#374049] hover:bg-[#24c3be]/10"
                                }`}
                              >
                                {editingForm.showVariantField
                                  ? "✓ Variant"
                                  : "+ Variant"}
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  setEditingForm({
                                    ...editingForm,
                                    showCollectionsField:
                                      !editingForm.showCollectionsField,
                                  })
                                }
                                className={`rounded-xl border px-3 py-2 text-xs font-medium transition ${
                                  editingForm.showCollectionsField
                                    ? "border-[#24c3be] bg-[#24c3be] text-white"
                                    : "border-[#93ca87]/40 bg-white text-[#374049] hover:bg-[#24c3be]/10"
                                }`}
                              >
                                {editingForm.showCollectionsField
                                  ? "✓ Collections"
                                  : "+ Collections"}
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  setEditingForm({
                                    ...editingForm,
                                    showNotesField: !editingForm.showNotesField,
                                  })
                                }
                                className={`rounded-xl border px-3 py-2 text-xs font-medium transition ${
                                  editingForm.showNotesField
                                    ? "border-[#24c3be] bg-[#24c3be] text-white"
                                    : "border-[#93ca87]/40 bg-white text-[#374049] hover:bg-[#24c3be]/10"
                                }`}
                              >
                                {editingForm.showNotesField
                                  ? "✓ Notes"
                                  : "+ Notes"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ================= OPTIONAL FIELDS ================= */}
                    {(editingForm.showVariantField ||
                      editingForm.showCollectionsField ||
                      editingForm.showNotesField) && (
                      <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
                        {editingForm.showVariantField && (
                          <div className="relative min-w-0">
                            <div className="mb-1 flex items-center justify-between gap-3">
                              <div className="text-xs font-semibold text-slate-600">
                                Variant
                              </div>

                              <button
                                type="button"
                                onClick={() =>
                                  setEditingForm({
                                    ...editingForm,
                                    showVariantField: false,
                                  })
                                }
                                className="text-xs text-slate-400 transition hover:text-red-500"
                              >
                                ✕
                              </button>
                            </div>

                            <input
                              value={editingForm.variant || ""}
                              onChange={(e) =>
                                setEditingForm({
                                  ...editingForm,
                                  variant: e.target.value,
                                })
                              }
                              className="block h-[48px] w-full min-w-0 truncate rounded-xl border border-[#93ca87]/40 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                              placeholder="e.g., bicolor"
                            />
                          </div>
                        )}

                        {editingForm.showCollectionsField && (
                          <div className="relative min-w-0">
                            <div className="mb-1 flex items-center justify-between gap-3">
                              <div className="text-xs font-semibold text-slate-600">
                                Collections
                              </div>

                              <button
                                type="button"
                                onClick={() =>
                                  setEditingForm({
                                    ...editingForm,
                                    showCollectionsField: false,
                                  })
                                }
                                className="text-xs text-slate-400 transition hover:text-red-500"
                              >
                                ✕
                              </button>
                            </div>

                            <select
                              value=""
                              onChange={(e) => {
                                const value = e.target.value;

                                if (value === "__new__") {
                                  const name = prompt("New collection name:");
                                  if (!name) return;

                                  setEditingForm({
                                    ...editingForm,
                                    collections: [
                                      ...new Set([
                                        ...(editingForm.collections || []),
                                        name,
                                      ]),
                                    ],
                                  });

                                  return;
                                }

                                if (!value) return;

                                const exists = (
                                  editingForm.collections || []
                                ).includes(value);

                                setEditingForm({
                                  ...editingForm,
                                  collections: exists
                                    ? editingForm.collections.filter(
                                        (c) => c !== value,
                                      )
                                    : [
                                        ...(editingForm.collections || []),
                                        value,
                                      ],
                                });
                              }}
                              className="block h-[48px] w-full min-w-0 truncate rounded-xl border border-[#93ca87]/40 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                            >
                              <option value="">Select collection</option>
                              <option value="__new__">
                                + Create new collection
                              </option>

                              {Object.keys(collections)
                                .sort()
                                .map((c) => (
                                  <option key={c} value={c}>
                                    {c}
                                  </option>
                                ))}
                            </select>

                            {editingForm.collections?.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {editingForm.collections.map((c) => (
                                  <button
                                    key={c}
                                    type="button"
                                    onClick={() =>
                                      setEditingForm({
                                        ...editingForm,
                                        collections:
                                          editingForm.collections.filter(
                                            (x) => x !== c,
                                          ),
                                      })
                                    }
                                    className="max-w-full truncate rounded-lg border border-[#93ca87]/40 bg-white px-2.5 py-1 text-xs text-[#374049] transition hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                                  >
                                    {c} ✕
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {editingForm.showNotesField && (
                          <div className="relative min-w-0 md:col-span-2">
                            <div className="mb-1 flex items-center justify-between gap-3">
                              <div className="text-xs font-semibold text-slate-600">
                                Notes
                              </div>

                              <div className="flex items-center gap-3">
                                <span className="text-[10px] text-slate-400">
                                  {(editingForm.notes || "").length}/2000
                                </span>

                                <button
                                  type="button"
                                  onClick={() =>
                                    setEditingForm({
                                      ...editingForm,
                                      showNotesField: false,
                                    })
                                  }
                                  className="text-xs text-slate-400 transition hover:text-red-500"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>

                            <textarea
                              value={editingForm.notes || ""}
                              onChange={(e) =>
                                setEditingForm({
                                  ...editingForm,
                                  notes: e.target.value.slice(0, 2000),
                                })
                              }
                              rows={4}
                              maxLength={2000}
                              placeholder="Add field observations, behavior, habitat details, or anything else relevant to this photo."
                              className="w-full min-w-0 resize-y rounded-xl border border-[#93ca87]/40 bg-white px-4 py-3 text-sm text-[#374049] focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {renamingPhoto && editingSpecies && (
              <div className="max-w-2xl mx-auto mt-4 mb-6 space-y-4 rounded-2xl bg-white border border-[#93CA87]/40 shadow-sm p-4">
                {!isOwnProfile && canReclassifyObservations && (
                  <div className="rounded-xl border border-[#24C3BE]/35 bg-[#FCFFF6] px-4 py-3 text-center">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#01767A]">
                      Administrator Taxonomic Correction
                    </div>

                    <p className="mt-1 text-xs leading-relaxed text-[#374049]/65">
                      The selected observation will be moved to the corrected
                      species record. Existing target records will be merged
                      automatically.
                    </p>
                  </div>
                )}

                {renameError && (
                  <div className="text-sm text-red-700 text-center">
                    {renameError}
                  </div>
                )}

                <div className="relative">
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => {
                      setRenameValue(e.target.value);
                      updateRenameSuggestions(e.target.value, "scientific");
                    }}
                    onKeyDown={handleRenameKeyDown}
                    className="w-full border border-slate-300 rounded-xl px-4 py-2"
                    placeholder="Genus species"
                    disabled={renameSaving}
                  />

                  {renameActiveField === "scientific" &&
                    renameSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 z-20 w-full border border-slate-200 rounded-xl mt-1 bg-white max-h-40 overflow-y-auto shadow-sm">
                        {renameSuggestions.map((sp, i) => (
                          <div
                            key={sp.scientific}
                            onClick={() => selectRenameSuggestion(sp)}
                            className={`px-3 py-2 text-sm cursor-pointer ${
                              renameActiveIndex === i
                                ? "bg-slate-200"
                                : "hover:bg-slate-100"
                            }`}
                          >
                            <span className="italic">{sp.scientific}</span>
                            <span className="text-slate-500 ml-2">
                              — {sp.common}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                </div>

                <div className="relative">
                  <input
                    type="text"
                    value={renameCommon}
                    onChange={(e) => {
                      setRenameCommon(e.target.value);
                      updateRenameSuggestions(e.target.value, "common");
                    }}
                    onKeyDown={handleRenameKeyDown}
                    className="w-full border border-slate-300 rounded-xl px-4 py-2"
                    placeholder="Common name"
                    disabled={renameSaving}
                  />

                  {renameActiveField === "common" &&
                    renameSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 z-20 w-full border border-slate-200 rounded-xl mt-1 bg-white max-h-40 overflow-y-auto shadow-sm">
                        {renameSuggestions.map((sp, i) => (
                          <div
                            key={sp.scientific}
                            onClick={() => selectRenameSuggestion(sp)}
                            className={`px-3 py-2 text-sm cursor-pointer ${
                              renameActiveIndex === i
                                ? "bg-slate-200"
                                : "hover:bg-slate-100"
                            }`}
                          >
                            <span className="italic">{sp.scientific}</span>
                            <span className="text-slate-500 ml-2">
                              — {sp.common}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                </div>

                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => handleRenameProfilePhoto(favViewForm.url)}
                    disabled={renameSaving}
                    className="rounded-full bg-[#24c3be] px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-[#1faeaa] transition disabled:opacity-60"
                  >
                    {renameSaving
                      ? isOwnProfile
                        ? "Renaming..."
                        : "Reclassifying..."
                      : isOwnProfile
                        ? "Save"
                        : "Apply Taxonomic Correction"}
                  </button>

                  <button
                    onClick={exitPhotoRename}
                    disabled={renameSaving}
                    className="rounded-full bg-white px-4 py-2 text-xs font-medium text-slate-800 border border-slate-300 shadow-sm hover:bg-slate-50 transition disabled:opacity-60"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!editingForm && !renamingPhoto && (
              <div className="mt-4 px-1 sm:px-2">
                <div className="rounded-2xl bg-white/80 border border-[#93CA87]/40 shadow-sm p-4">
                  {displayLocation(favViewForm) &&
                    displayLocation(favViewForm) !== "Unknown" && (
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 text-slate-400">⌖</div>

                        <div className="min-w-0">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Location
                          </div>

                          <div className="text-sm text-[#374049] leading-snug">
                            {displayLocation(favViewForm)}
                            {hasMappedLocation(favViewForm) && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-[#24C3BE]/10 border border-[#24C3BE]/30 px-2 py-[1px] text-[10px] font-medium text-[#24C3BE]">
                                ✓
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    {formatDateForDisplay(favViewForm?.date) !== "Unknown" && (
                      <div className="rounded-full bg-[#374049] border border-[#93ca87]/40 px-3 py-1.5 text-xs text-white font-medium">
                        <span className="text-[#24C3BE]">Date</span>
                        <span className="mx-1.5 text-[#93CA87]">•</span>
                        {formatDateForDisplay(favViewForm?.date)}
                      </div>
                    )}

                    {favViewForm?.label &&
                      favViewForm.label.trim().toLowerCase() !== "default" && (
                        <div className="rounded-full bg-[#374049] border border-[#93ca87]/40 px-3 py-1.5 text-xs text-white font-medium">
                          <span className="text-[#24C3BE]">Lifeform</span>
                          <span className="mx-1.5 text-[#93CA87]">•</span>
                          {favViewForm.label}
                        </div>
                      )}

                    {favViewForm?.variant && favViewForm.variant !== "NA" && (
                      <div className="rounded-full bg-[#374049] border border-[#93ca87]/40 px-3 py-1.5 text-xs text-white font-medium">
                        <span className="text-[#24C3BE]">Variant</span>
                        <span className="mx-1.5 text-[#93CA87]">•</span>
                        {favViewForm.variant}
                      </div>
                    )}

                    {Array.isArray(favViewForm?.collections) &&
                      favViewForm.collections.length > 0 && (
                        <div className="relative">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFavCollectionsPopoverOpen((open) => !open);
                            }}
                            className="max-w-full break-words rounded-full bg-[#374049] border border-[#93ca87]/40 px-3 py-1.5 text-xs text-white font-medium"
                          >
                            <span className="text-[#24C3BE]">Collections</span>
                          </button>

                          {favCollectionsPopoverOpen && (
                            <div
                              className="absolute right-0 bottom-full z-40 mb-2 w-max max-w-[16rem] rounded-2xl border border-[#93ca87]/40 bg-white p-3 text-xs text-[#374049] shadow-xl"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex w-max max-w-full flex-wrap gap-1.5">
                                {favViewForm.collections.map(
                                  (collectionName) => (
                                    <span
                                      key={collectionName}
                                      className="rounded-full bg-[#374049] border border-[#93ca87]/40 px-2 py-1 text-xs text-white font-medium"
                                    >
                                      {collectionName}
                                    </span>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                  </div>

                  <PlantSpeciesDescription
                    scientificName={favViewSpecies.name}
                    enabled={isPlantSpecies}
                    detailButton
                  />

                  {favViewForm?.notes?.trim() && (
                    <div className="mt-4 border-t border-[#93CA87]/30 pt-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 text-[#24C3BE]">✎</div>

                        <div className="w-0 min-w-0 flex-1">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                            Notes
                          </div>

                          <p className="mt-1 max-w-full whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700 [overflow-wrap:anywhere]">
                            {favViewForm.notes}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <EcologicalRelationships
                    scientificName={favViewSpecies.name}
                    commonName={favViewSpecies.common || ""}
                    kingdom={viewedTaxonomy?.kingdom || ""}
                    collectionOwnerUid={collectionOwnerUid}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
